import moment from 'moment';
import { AssignmentDetails } from '../app/store/Slices/AssignmentDetailsSlice';
import { GridCellState } from '../app/store/Slices/GridCellSlice';
import { MergeBlocks, TimeBlock } from './DateUtils';
import { NonProjectDates } from '../app/contracts/EditAvailabilityContracts';
import { Constants } from '../common/constants';
import { CEEventDetails } from '../common/Contracts';
import { isSameDay, getHoursBetween } from './DateUtils';
import { endOfDay, parse, startOfDay } from 'date-fns';

export function calculateBookedHours(
  assignmentDetails: AssignmentDetails,
  dailyDateList: string[],
  cellDetails: GridCellState[]
): { dailyDate: string; bookedHours: number }[] {
  return dailyDateList.map((date) => {
    let bookedHours = 0;
    if (!assignmentDetails)
      return { dailyDate: date, bookedHours: 0 };
    const updatedCells = cellDetails.filter((cell) => cell.columnId === date);
    let publicHolidays = assignmentDetails?.eventDetails?.filter((event) => {
      return event.eventCategory.toLowerCase() === 'publicholiday'
    });
    let vacations = assignmentDetails?.eventDetails?.filter((event) => {
      return event.eventCategory.toLowerCase() === 'msvacation'
    });
    const vacationHours = calculateVacationHours(vacations, date);
    const publicHolidayHours = calculatePublicHolidayHours(publicHolidays, date);
    bookedHours += vacationHours + publicHolidayHours;

    let nonProjectEventsOnADay = assignmentDetails?.eventDetails?.filter((assignment) => {
      return assignment.eventCategory.toLowerCase() === 'nonproject';
    }) || [];
    let nonProjectDates: NonProjectDates[] = nonProjectEventsOnADay.map((event) => {
      return {
        startDateTime: new Date(event.startDateTime),
        endDateTime: new Date(event.endDateTime),
        npRequestId: event.customProperties?.ResourceRequestId
      };
    });


    let outlookEventsOnaDay = assignmentDetails?.outlookCalendarDetails?.eventDetails?.filter((e) => {
      return startOfDay(new Date(e.startDateTime)) <= startOfDay(new Date(date)) && startOfDay(new Date(e.endDateTime)) >= startOfDay(new Date(date))
    });

    const projectEvents = assignmentDetails?.eventDetails?.filter(event => event.eventCategory.toLowerCase() === "project");
    let projectsOnADay = projectEvents?.filter(event =>
      startOfDay(new Date(event.customProperties?.ProjectStartDate)) <= startOfDay(new Date(date)) &&
      startOfDay(new Date(event.customProperties?.ProjectEndDate)) >= startOfDay(new Date(date))
    );


    if (outlookEventsOnaDay.length > 0) {
      const isBusinessHoursEnabled = assignmentDetails?.outlookCalendarDetails?.isBusinessHoursEnabled || false;
      const businessStartTime = isBusinessHoursEnabled? parse(assignmentDetails?.outlookCalendarDetails?.workPreferences?.startTime, "HH:mm:ss", new Date(date)): startOfDay(new Date(date));
      const businessEndTime = isBusinessHoursEnabled? parse(assignmentDetails?.outlookCalendarDetails?.workPreferences?.endTime, "HH:mm:ss", new Date(date)): endOfDay(new Date(date)) ;
      const outlookBusyBlocks = outlookEventsOnaDay
        .filter(e =>
          (e.status === 'Busy' || e.status === 'Oof') &&
          new Date(e.endDateTime) > businessStartTime &&
          new Date(e.startDateTime) < businessEndTime
        )
        .map(e => {
          const start = new Date(e.startDateTime) < businessStartTime ? businessStartTime : e.startDateTime;
          const end = new Date(e.endDateTime) > businessEndTime ? businessEndTime : e.endDateTime;
          return new TimeBlock(start, end);
        });
      let npTimeBlocks = nonProjectDates?.length > 0 ? getNonProjectBlocks(nonProjectDates, date) : [];
      let projectTimeBlocks = projectsOnADay?.length > 0 ? getGRMBlocksFromProjectAssignmentDetails(projectsOnADay, date, updatedCells) : [];
      let allGrmBlocks = [...projectTimeBlocks, ...npTimeBlocks];
      // Filter out outlook blocks that overlap with any GRM block
      const filteredOutlook = outlookBusyBlocks.filter(
        o => !allGrmBlocks.some(g => g.overlapsWith(o))
      );
      // Merge the filtered outlook blocks
      const mergedOutlook = MergeBlocks(filteredOutlook);
      // Sum up the hours from all GRM blocks and merged outlook blocks
      const grmHours = allGrmBlocks.reduce((sum, b) => sum + (b.duration/3600000), 0);
      const outlookHours = mergedOutlook.reduce((sum, b) => sum + (b.duration/3600000), 0);
      bookedHours += grmHours + outlookHours;

    } else {
      let projectHours = projectsOnADay?.reduce((sum, project) => {
        const start = project.customProperties?.ScheduledStartDate;
        const end = project.customProperties?.ScheduledEndDate;
        const gridCell = updatedCells.find(cell => cell.rowId === project.customProperties?.ResourceRequestId.toString());
        if (isSameDay(start, end))
          return sum +(gridCell? gridCell.currValue : getHoursBetween(start, end)) ;
        else
        {
          const todaysSchedule = project?.customProperties?.Schedules?.find(s => isSameDay(s.forecastDate, date));
          return sum + (gridCell? gridCell.currValue :(todaysSchedule?.durationInHours || 0));
        }
          
      }, 0);
      let nonProjectHours = nonProjectDates?.reduce((sum, nonProject) => {
        return moment(nonProject.startDateTime).format(Constants.DATE_FORMAT) === date ? (new Date(nonProject.endDateTime).getTime() - new Date(nonProject.startDateTime).getTime()) / (1000 * 60 * 60) : 0 + sum;
      }, 0);
      bookedHours += projectHours + nonProjectHours;
    }
    bookedHours = Number(bookedHours.toFixed(2));
    return { dailyDate: date, bookedHours };
  });
}

function getNonProjectBlocks(npDates: NonProjectDates[], date: string) {
  const workStart = new Date(date);
  workStart.setHours(Constants.WORK_START, 0, 0, 0);
  const workEnd = new Date(date);
  workEnd.setHours(Constants.WORK_END, 0, 0, 0);

  return npDates
    .filter(
      np =>
        startOfDay(np.startDateTime) <= startOfDay(workEnd) && startOfDay(np.endDateTime) >= startOfDay(workStart)
    )
    .map(np => {
      const start =
        startOfDay(np.startDateTime) < startOfDay(workStart) ? workStart : np.startDateTime;
      const end =
        startOfDay(np.endDateTime) > startOfDay(workEnd) ? workEnd : np.endDateTime;
      return new TimeBlock(start, end);
    });
}
function calculateVacationHours(vacations: CEEventDetails[], date: string): number {
  let vacationsHoursOnaDay = 0;
  if (!vacations || vacations.length === 0)
    return vacationsHoursOnaDay; // No vacations, return 0 
  // Case 1: Vacation starts and ends on the same day
  vacationsHoursOnaDay += vacations
    .filter(x => isSameDay(x.startDateTime, date) && isSameDay(x.endDateTime, date))
    .reduce((sum, x) => sum + getHoursBetween(x.startDateTime, x.endDateTime), 0);

  // Case 2: Vacation spans the whole day
  vacationsHoursOnaDay += vacations
    .filter(x => moment(x.startDateTime).format(Constants.DATE_FORMAT) < date && moment(x.endDateTime).format(Constants.DATE_FORMAT) > date)
    .reduce((sum, x) => sum + 8, 0); // 8 working hours

  // Case 3: Vacation starts today and ends later
  vacationsHoursOnaDay += vacations
    .filter(x => isSameDay(x.startDateTime, date) && moment(x.endDateTime).format(Constants.DATE_FORMAT) > date)
    .reduce((sum, x) => {
      const workDayEnd = new Date(date);
      workDayEnd.setHours(Constants.WORK_END, 0, 0, 0);
      return sum + getHoursBetween(x.startDateTime, workDayEnd.toString());
    }, 0);

  // Case 4: Vacation started earlier and ends today
  vacationsHoursOnaDay += vacations
    .filter(x => moment(x.startDateTime).format(Constants.DATE_FORMAT) < date && isSameDay(x.endDateTime, date))
    .reduce((sum, x) => {
      const workDayStart = new Date(date);
      workDayStart.setHours(Constants.WORK_START, 0, 0, 0);
      return sum + getHoursBetween(workDayStart.toString(), x.endDateTime);
    }, 0);

  return vacationsHoursOnaDay;
}
function calculatePublicHolidayHours(publicHolidays: CEEventDetails[], date: string): number {
  let publicHolidayHoursOnaDay = 0;
  if (!publicHolidays || publicHolidays.length === 0)
    return publicHolidayHoursOnaDay; // No public holidays, return 0 

  publicHolidays
    .filter(x => isSameDay(x.startDateTime, date) && isSameDay(x.endDateTime, date))
    .reduce((sum, x) => sum + getHoursBetween(x.startDateTime, x.endDateTime), 0);
  return publicHolidayHoursOnaDay;
}

function getGRMBlocksFromProjectAssignmentDetails(assignments: CEEventDetails[], date, gridCells: GridCellState[]) {
  const blocks = [];
  const WORK_START_HOUR = Constants.WORK_START;
  assignments.forEach(assignment => {
    const RequestedStart = assignment.customProperties?.ScheduledStartDate;
    const RequestedEnd = assignment.customProperties?.ScheduledEndDate;
    const updatedGridCell = gridCells.find(cell => cell.rowId === String(assignment.customProperties?.ResourceRequestId));

    // Case 1: Use ScheduledStartDate and ScheduledEndDate if both fall on the same date
    if (
      RequestedStart &&
      RequestedEnd &&
      isSameDay(RequestedStart, date) &&
      isSameDay(RequestedEnd, date)
    ) {
      if (new Date(RequestedEnd) > new Date(RequestedStart)) {
        let end = new Date(RequestedEnd);
        if( updatedGridCell) {
          end.setHours(new Date(RequestedStart).getHours() + updatedGridCell.currValue);
        }
        blocks.push(new TimeBlock(RequestedStart, end));
      }
    } else {
      // Case 2: Use per-day schedules
      const todaysSchedule = assignment?.customProperties?.Schedules?.find(s => isSameDay(s.forecastDate, date));
      if (todaysSchedule) {
        // Start at 9 AM
        const start = new Date(date);
        start.setHours(WORK_START_HOUR, 0, 0, 0);
        // End at 9 AM + duration
        const end = new Date(start);
        end.setHours(start.getHours() + (updatedGridCell ? updatedGridCell.currValue : todaysSchedule.durationInHours));
        blocks.push(new TimeBlock(start, end));
      }
    }
  });

  return blocks;
}
